import { apiClient, type ApiResponse } from './client'

export type ContentFeeLevel = 'L3' | 'L4' | 'L5'

export interface ContentFeeTier {
  level: string
  price: number
  currency: string
  grantedLessons: number
}

/** 결제로 종료될 기존 현재 수강. */
export interface CancellingEnrollment {
  enrollmentId: number
  licenseId: number
  productName: string | null
  status: string
}

/** 이번 결제가 현재 수강에 미치는 영향. 무료체험 전환 견적에만 채워진다. */
export interface ContentFeeQuoteEnrollmentImpact {
  confirmedLicenseId: number
  confirmedProductName: string | null
  cancellingEnrollments: CancellingEnrollment[]
  trialClosing: boolean
}

export interface ContentFeeQuote {
  contentId: number | null
  contentTitle: string | null
  derivable: boolean
  reason: string | null
  tiers: ContentFeeTier[]
  enrollmentImpact?: ContentFeeQuoteEnrollmentImpact | null
  /** 서버가 발급한 견적 지문. derivable=true면 존재하며, purchase body에 그대로 전달한다. */
  quoteFingerprint?: string | null
}

export interface PurchaseContentFeeRequest {
  level: string
  billingKeyId: number | null
  purchaseAttemptId: string
  memo: string | null
  formalLicenseId?: number | null
  /** GET quote의 quoteFingerprint. 불일치 시 서버가 CONTENT_FEE_QUOTE_STALE(409)로 거부한다. */
  quoteFingerprint?: string | null
}

export interface RefundContentFeeRequest {
  memo: string | null
}

export interface RegisterBillingKeyRequest {
  cardNumber: string
  cardExpirationYear: string
  cardExpirationMonth: string
  customerIdentityNumber: string
  cardPassword: string
  cardAlias: string | null
}

export interface BillingKeyResponse {
  id: number
  provider: string
  providerMode: string
  cardLast4: string | null
  cardCompanyCode: string | null
  cardCompanyName: string | null
  cardAlias: string | null
  active: boolean
  usable: boolean
  registeredByUserId: number
  createdAt: string
}

export interface ContentFeeEntitlementResponse {
  entitlementId: number
  beneficiaryUserId: number
  level: string
  courseGroup: string | null
  contentId: number | null
  contentTitle: string | null
  grantedLessons: number
  carriedOver: number
  usedLessons: number
  remaining: number
  /** 환불되지 않은 결제 전체의 지급 수 합(이번 결제 포함, 이월 제외) */
  cumulativeGrantedLessons: number
  /** 환불되지 않은 결제 전체의 사용 수 합(이번 결제 포함) */
  cumulativeUsedLessons: number
  periodStart: string | null
  periodEnd: string | null
  graceUntil: string | null
  status: string
  orderId: number | null
  paidAt: string | null
}

export interface ContentFeeOrderRow {
  entitlementId: number
  beneficiaryUserId: number
  beneficiaryName: string | null
  beneficiaryEmail: string | null
  level: string
  grantedLessons: number
  carriedOver: number
  usedLessons: number
  remainingLessons: number
  amount: number | null
  status: string
  paidAt: string | null
  refundedAt: string | null
  orderId: number | null
  pgProvider: string | null
  pgTransactionId: string | null
  cardLast4Digits: string | null
}

const basePath = (userId: number) => `/lc/admin/students/${userId}/content-fee`

export const contentFeeApi = {
  getQuote: (userId: number, formalLicenseId?: number) =>
    apiClient.get<ApiResponse<ContentFeeQuote>>(`${basePath(userId)}/quote`, {
      params: formalLicenseId ? { formalLicenseId } : undefined,
    }),
  purchase: (userId: number, body: PurchaseContentFeeRequest) =>
    apiClient.post<ApiResponse<ContentFeeEntitlementResponse>>(`${basePath(userId)}/purchase`, body),
  refund: (userId: number, body: RefundContentFeeRequest) =>
    apiClient.post<ApiResponse<ContentFeeEntitlementResponse>>(`${basePath(userId)}/refund`, body),
  getCurrent: (userId: number) =>
    apiClient.get<ApiResponse<ContentFeeEntitlementResponse | null>>(basePath(userId)),
  getOrders: (userId?: number) =>
    apiClient.get<ApiResponse<ContentFeeOrderRow[]>>(`/lc/admin/content-fee/orders`, {
      params: userId ? { userId } : undefined,
    }),
  getBillingKeys: () =>
    apiClient.get<ApiResponse<BillingKeyResponse[]>>('/lc/admin/billing-keys'),
  registerBillingKey: (body: RegisterBillingKeyRequest) =>
    apiClient.post<ApiResponse<BillingKeyResponse>>('/lc/admin/billing-keys', body),
  deleteBillingKey: (id: number) =>
    apiClient.delete<ApiResponse<void>>(`/lc/admin/billing-keys/${id}`),
}
